import {
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Avatar,
  Box,
  Divider,
  Tag,
  Link,
  LinkBox,
  LinkOverlay,
} from "@chakra-ui/react";
import { json, redirect } from "@remix-run/node";
import { useLoaderData, Link as RemixLink } from "@remix-run/react";
import { MdArrowBack } from "react-icons/md";
import {
  MarkdownRenderer,
  BlogEmailCapture,
  ImagePlaceholder,
} from "#app/components/blog/index.js";
import { Paper } from "#app/components/Paper.jsx";
import { CallToAction } from "#app/components/CallToAction.jsx";
import { formatLongDate } from "#app/utils/dateFormat.js";
import { useLocale } from "#app/context/LocaleContext.jsx";
import {
  getBlogPost,
  getRelatedPosts,
  getCTAConfig,
  REGION_LABELS,
} from "#app/utils/blog.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { getBlogPostingSchema } from "#app/utils/structuredData.js";
import { handleEmailCapture } from "#app/utils/emailCaptureAction.server.js";
import { withSecurityHeaders } from "#app/utils/security.server.js";

export const meta = ({ data, matches }) => {
  const baseUrl = getBaseUrl(matches);

  if (!data?.post) {
    return getSeoMeta({
      title: "Blog Post Not Found - Schnerp",
      description: "The blog post you are looking for could not be found.",
      image: getDefaultOgImage(baseUrl),
    });
  }

  const { post } = data;
  // Prefer a per-post canonical override when set (frontmatter `canonicalPath`).
  // Used by the Alberta Parks update post to redirect SEO weight onto the
  // evergreen /alberta-parks explainer page.
  const canonicalUrl = post.canonicalPath
    ? `${baseUrl}${post.canonicalPath}`
    : `${baseUrl}/blog/${post.slug}`;
  return getSeoMeta({
    title: `${post.title} - Schnerp Blog`,
    description: post.excerpt,
    url: canonicalUrl,
    image: post.featuredImage || getDefaultOgImage(baseUrl),
    type: "article",
    publishedTime: post.publishedAt,
    modifiedTime: post.updatedAt,
    tags: post.tags,
  });
};

export const headers = ({ loaderHeaders, parentHeaders }) =>
  withSecurityHeaders({
    ...Object.fromEntries(parentHeaders),
    "Cache-Control": loaderHeaders.get("Cache-Control"),
  });

// Retired posts keep their URL alive with a permanent redirect to the page
// that now tells the story, so links and search results still land somewhere
// true. alberta-parks-update: the /alberta-parks page carries the full account
// of why Schnerp stopped scanning Alberta Parks and how to get alerts there.
const RETIRED_POSTS = {
  "alberta-parks-update": "/alberta-parks",
};

export const loader = async ({ params }) => {
  const { slug } = params;
  if (RETIRED_POSTS[slug]) throw redirect(RETIRED_POSTS[slug], 301);
  const post = await getBlogPost(slug);

  if (!post) {
    throw new Response("Blog post not found", { status: 404 });
  }

  const relatedPosts = await getRelatedPosts(slug, post.region, 3);
  const ctaConfig = getCTAConfig(post);

  return json(
    { post, relatedPosts, ctaConfig },
    {
      headers: {
        "Cache-Control":
          "public, max-age=0, s-maxage=86400, stale-while-revalidate=86400",
      },
    },
  );
};

export const action = async ({ request, params }) => {
  const { slug } = params;
  const post = await getBlogPost(slug);
  if (!post?.emailCapture?.agency) {
    return json({ error: "Not configured" }, { status: 400 });
  }

  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent === "emailCapture") {
    return handleEmailCapture(formData, { agency: post.emailCapture.agency });
  }

  return json({ error: "Unknown intent" }, { status: 400 });
};

/**
 * Parse content into segments of markdown text and component markers.
 * Markers use the format: <!-- component:type -->
 * Returns an array of { type: "markdown", content } or { type: "component", name }.
 */
function parseContentSegments(content) {
  const COMPONENT_RE = /<!-- component:([\w-]+) -->/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = COMPONENT_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "markdown",
        content: content
          .slice(lastIndex, match.index)
          .replace(/<!--[\s\S]*?-->/g, ""),
      });
    }
    segments.push({ type: "component", name: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: "markdown",
      content: content.slice(lastIndex).replace(/<!--[\s\S]*?-->/g, ""),
    });
  }

  return segments;
}

export default function BlogPost() {
  const { post, relatedPosts, ctaConfig } = useLoaderData();
  const locale = useLocale();
  const {
    title,
    publishedAt,
    updatedAt,
    readTime,
    author,
    content,
    region,
    emailCapture,
    featuredImageCredit,
    featuredImageCreditUrl,
  } = post;

  const segments = parseContentSegments(content);

  const backTo = region ? `/blog?region=${region}` : "/blog";
  const regionLabel = region ? REGION_LABELS[region] : null;

  const baseUrl =
    typeof window !== "undefined"
      ? window.ENV?.BASE_URL || window.location.origin
      : process.env.BASE_URL || "https://schnerp.com";

  const blogPostSchema = getBlogPostingSchema({
    title,
    excerpt: post.excerpt,
    publishedAt,
    updatedAt,
    author,
    url: `${baseUrl}/blog/${post.slug}`,
    image: post.featuredImage,
  });

  return (
    <Container maxW="4xl">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostSchema) }}
      />
      <VStack spacing={6} align="stretch">
        {/* Back button */}
        <Box>
          <Button
            as={RemixLink}
            to={backTo}
            variant="ghost"
            leftIcon={<Icon as={MdArrowBack} />}
            colorScheme="brand"
          >
            Back to Blog
          </Button>
        </Box>

        <Paper>
          {/* Featured image */}
          {post.featuredImage && (
            <Box mx={-6} mt={-6} mb={6}>
              <Box
                as="img"
                src={post.featuredImage}
                alt={title}
                w="full"
                maxH="400px"
                objectFit="cover"
                roundedTop="xl"
              />
              {featuredImageCredit && (
                <Text
                  fontSize="2xs"
                  color="subtle"
                  px={6}
                  pt={1.5}
                  sx={{
                    // Blend the links into the caption: same subtle grey, a
                    // faint underline, only lifting on hover. Attribution
                    // stays present and clickable without pulling the eye.
                    a: {
                      color: "inherit",
                      textDecoration: "underline",
                      textDecorationColor: "currentcolor",
                      textUnderlineOffset: "2px",
                      opacity: 0.85,
                    },
                    "a:hover": { opacity: 1, color: "muted" },
                  }}
                >
                  Photo by{" "}
                  {featuredImageCreditUrl ? (
                    <Link
                      href={featuredImageCreditUrl}
                      isExternal
                      rel="nofollow noopener"
                    >
                      {featuredImageCredit}
                    </Link>
                  ) : (
                    featuredImageCredit
                  )}
                  {/* Unsplash's attribution guideline: link the photographer's
                      profile AND link back to Unsplash, both with utm referral.
                      The "on Unsplash" half only shows for Unsplash credits, so
                      UGC/other sources render as a plain "Photo by <name>". */}
                  {featuredImageCreditUrl?.includes("unsplash.com") && (
                    <>
                      {" "}
                      on{" "}
                      <Link
                        href="https://unsplash.com/?utm_source=schnerp&utm_medium=referral"
                        isExternal
                        rel="nofollow noopener"
                      >
                        Unsplash
                      </Link>
                    </>
                  )}
                </Text>
              )}
            </Box>
          )}

          {/* Article header */}
          <VStack spacing={3} align="stretch">
            <HStack color="muted" fontSize="sm" flexWrap="wrap">
              <Text>{formatLongDate(publishedAt, locale)}</Text>
              {updatedAt && updatedAt !== publishedAt && (
                <>
                  <Text>•</Text>
                  <Text>Updated {formatLongDate(updatedAt, locale)}</Text>
                </>
              )}
              <Text>•</Text>
              <Text>{readTime} min read</Text>
              {regionLabel && (
                <>
                  <Text>•</Text>
                  <Tag size="sm" variant="outline" colorScheme="brand">
                    {regionLabel}
                  </Tag>
                </>
              )}
            </HStack>

            <Heading as="h1" size="md" lineHeight="1.3">
              {title}
            </Heading>

            {author && (
              <HStack spacing={3} minW="0" flex="1">
                <Avatar size="sm" name={author.name} src={author.avatar} />
                <VStack align="start" spacing={0} minW="0">
                  <Text fontWeight="medium" fontSize="sm">
                    {author.name}
                  </Text>
                  <Text fontSize="xs" color="muted" noOfLines={1}>
                    {author.title}
                  </Text>
                </VStack>
              </HStack>
            )}

            <Divider my={4} />
          </VStack>

          {/* Article content */}
          <Box>
            {segments.map((segment, i) => {
              if (segment.type === "markdown") {
                return (
                  <MarkdownRenderer key={i}>{segment.content}</MarkdownRenderer>
                );
              }
              if (segment.name === "email-capture" && emailCapture) {
                return <BlogEmailCapture key={i} config={emailCapture} />;
              }
              return null;
            })}
          </Box>
        </Paper>

        {/* CTA */}
        <CallToAction
          heading={ctaConfig.heading}
          body={ctaConfig.body}
          buttonText={ctaConfig.buttonText}
          buttonProps={{
            as: RemixLink,
            to: ctaConfig.buttonLink,
          }}
        />

        {/* More from [region] */}
        {regionLabel && relatedPosts.length >= 2 && (
          <Box>
            <Heading as="h2" size="xs" mt={8} mb={4}>
              More from {regionLabel}
            </Heading>
            <Paper p={0}>
              <VStack spacing={0} divider={<Divider />} align="stretch">
                {relatedPosts.map((related) => (
                  <LinkBox
                    key={related.slug}
                    p={4}
                    _hover={{ bg: "bg-subtle" }}
                    _focusWithin={{ bg: "bg-subtle" }}
                    transition="background 0.2s"
                  >
                    <HStack spacing={4} align="start">
                      <Box
                        flexShrink={0}
                        w={{ base: "84px", sm: "120px" }}
                        sx={{ aspectRatio: "16/9" }}
                        position="relative"
                      >
                        {related.featuredImage ? (
                          <Box
                            as="img"
                            src={related.featuredImage}
                            alt=""
                            position="absolute"
                            inset="0"
                            w="full"
                            h="full"
                            objectFit="cover"
                            rounded="md"
                          />
                        ) : (
                          <ImagePlaceholder
                            position="absolute"
                            inset="0"
                            rounded="md"
                          />
                        )}
                      </Box>
                      <Box minW="0">
                        <Heading as="h3" size="xs" mb={1}>
                          <LinkOverlay
                            as={RemixLink}
                            to={`/blog/${related.slug}`}
                          >
                            {related.title}
                          </LinkOverlay>
                        </Heading>
                        <Text fontSize="sm" color="muted" noOfLines={2}>
                          {related.excerpt}
                        </Text>
                      </Box>
                    </HStack>
                  </LinkBox>
                ))}
              </VStack>
            </Paper>
          </Box>
        )}

        {/* Footer back button */}
        <Box>
          <Button
            as={RemixLink}
            to={backTo}
            variant="ghost"
            leftIcon={<Icon as={MdArrowBack} />}
            colorScheme="brand"
          >
            Back to Blog
          </Button>
        </Box>
      </VStack>
    </Container>
  );
}
